import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { UploadCloud, FileCheck2, ShieldCheck, CheckCircle2, ArrowRight, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal } from "@/components/shared/Reveal";
import { StatCounter } from "@/components/shared/StatCounter";
import { SavingsEstimator } from "@/components/shared/SavingsEstimator";
import { toast } from "sonner";
import { SPEND_TIERS } from "@/data/content";
import { submitAuditLead, isBusinessEmail } from "@/lib/leads";

const EMPTY = { name: "", company: "", email: "", phone: "", spend: "", carriers: "", fileName: "" };

export default function BillingBreakthrough() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formStartedAt, setFormStartedAt] = useState(() => Date.now());
  const [website, setWebsite] = useState("");
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setErrors((e) => ({ ...e, file: "Please provide a valid file under 10MB." }));
      return;
    }
    setErrors((e) => ({ ...e, file: undefined }));
    setSelectedFile(file);
    set("fileName", file.name);
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().split(" ").filter(Boolean).length < 2) e.name = "Please enter your full first and last name.";
    if (form.company.trim().length < 2) e.company = "Company name is required.";
    if (!isBusinessEmail(form.email)) e.email = "Please enter a valid company business email.";
    if (!/^\+?[0-9\s().-]{7,}$/.test(form.phone)) e.phone = "Please enter a valid phone number.";
    if (!form.spend) e.spend = "Please select your monthly technology spend tier.";
    if (!selectedFile) e.file = "Please upload a PDF or Excel invoice under 10MB.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitAuditLead({ ...form, website, _formStartedAt: formStartedAt }, selectedFile);
      setDone(true);
    } catch (error) {
      toast.error("We couldn't upload the invoice.", { description: "Please try again or email us directly." });
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    toast.success("Invoice received", { description: "Your free audit is queued — results within one business day." });
  };

  return (
    <>
      <PageHero
        kicker="The Billing Breakthrough™"
        title="Receipts don't lie. We just read them carefully."
        subtitle="Mid-market teams routinely overpay for telecom, SaaS, and cloud. Our free TEM audit typically uncovers 20–40% in direct waste, billing errors, and auto-renewed bloat — in one business day."
      />

      {/* Stats strip */}
      <section className="section-x">
        <div className="container-tmt grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-3">
          {[{ v: "20-40%", l: "Typical direct savings" }, { v: "150+", l: "Audits delivered" }, { v: "1 Day", l: "Typical turnaround" }].map((s) => (
            <div key={s.l} className="bg-card p-7"><StatCounter value={s.v} label={s.l} /></div>
          ))}
        </div>
      </section>

      {/* Savings estimator */}
      <section className="section-x py-16 sm:py-24">
        <div className="container-tmt grid items-center gap-10 lg:grid-cols-[1fr_1.05fr]">
          <Reveal>
            <div>
              <span className="kicker">Before You Upload Anything</span>
              <h2 className="mt-4 font-display text-3xl font-semibold sm:text-4xl">
                Run the numbers in ten seconds.
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                No form, no email — just move the slider. It’s the same 20–40% recovery range we see
                across 150+ delivered audits. When the number looks worth it, confirm it with a free,
                supplier-neutral audit of your real invoices.
              </p>
              <ul className="mt-8 space-y-3">
                {["Based on real audit outcomes, not sales math", "Zero obligation to engage us afterward", "Your invoice is where the estimate becomes exact"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-foreground/90">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SavingsEstimator onCta={() => document.getElementById("audit-form")?.scrollIntoView({ behavior: "smooth", block: "center" })} />
          </Reveal>
        </div>
      </section>

      <section className="section-x py-16 sm:py-24" id="audit-form">
        <div className="container-tmt grid gap-10 lg:grid-cols-2">
          {/* Explainer */}
          <Reveal>
            <div>
              <h2 className="font-display text-3xl font-semibold sm:text-4xl">What we find in the fine print</h2>
              <ul className="mt-8 space-y-5">
                {[
                  ["Billing errors & phantom charges", "Circuits you decommissioned. Lines nobody uses. Taxes miscalculated."],
                  ["Auto-renewed contract bloat", "Evergreen clauses that quietly locked you into last year's rates."],
                  ["Rate & tier misalignment", "Overpaying for capacity and seats you've long outgrown — or never used."],
                  ["Redundant services", "Overlapping tools and duplicate providers hiding across departments."],
                ].map(([t, d]) => (
                  <li key={t} className="flex items-start gap-4">
                    <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-accent"><TrendingDown className="h-5 w-5" /></span>
                    <div>
                      <h3 className="font-display font-semibold">{t}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{d}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-5 py-4">
                <ShieldCheck className="h-6 w-6 shrink-0 text-primary" />
                <p className="text-sm text-foreground/90">The audit is completely free and 100% supplier-neutral. No obligation to engage us afterward.</p>
              </div>
            </div>
          </Reveal>

          {/* Form / Success */}
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-border bg-card p-7 shadow-elegant sm:p-9">
              {!done ? (
                <form onSubmit={submit} className="space-y-4">
                  <h3 className="font-display text-2xl font-semibold">Upload for your free audit</h3>
                  <div className="sr-only" aria-hidden="true">
                    <Label htmlFor="billing-website">Website</Label>
                    <Input
                      id="billing-website"
                      name="website"
                      value={website}
                      onChange={(event) => setWebsite(event.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Full name" error={errors.name}>
                      <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Rivera" />
                    </Field>
                    <Field label="Company" error={errors.company}>
                      <Input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Acme Corp" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Business email" error={errors.email}>
                      <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@company.com" />
                    </Field>
                    <Field label="Phone" error={errors.phone}>
                      <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" />
                    </Field>
                  </div>
                  <Field label="Estimated monthly telecom / IT spend" error={errors.spend}>
                    <Select value={form.spend} onValueChange={(v) => set("spend", v)}>
                      <SelectTrigger><SelectValue placeholder="Select a tier" /></SelectTrigger>
                      <SelectContent>{SPEND_TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Primary carriers (optional)">
                    <Textarea value={form.carriers} onChange={(e) => set("carriers", e.target.value)} placeholder="e.g. Verizon, AT&T, Comcast..." rows={2} maxLength={500} />
                  </Field>

                  {/* Drop zone */}
                  <div>
                    <Label className="text-sm text-foreground/90">Invoice upload</Label>
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                      onClick={() => fileRef.current?.click()}
                      className={`mt-1.5 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                        dragging ? "border-primary bg-primary/10" : form.fileName ? "border-success/50 bg-success/5" : "border-border bg-secondary/40 hover:border-primary/40"
                      }`}
                    >
                      {form.fileName ? (
                        <><FileCheck2 className="h-7 w-7 text-success" /><p className="mt-2 text-sm text-foreground">{form.fileName}</p><p className="mt-0.5 font-mono text-xs text-muted-foreground">Click to replace</p></>
                      ) : (
                        <><UploadCloud className="h-7 w-7 text-primary" /><p className="mt-2 text-sm text-foreground">Drop a PDF or Excel invoice here</p><p className="mt-0.5 font-mono text-xs text-muted-foreground">PDF, CSV, XLSX · under 10MB</p></>
                      )}
                      <input ref={fileRef} type="file" accept=".pdf,.csv,.xlsx" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                    </div>
                    {errors.file && <p className="mt-1 text-xs text-destructive">{errors.file}</p>}
                  </div>

                  <Button type="submit" variant="premium" size="lg" className="w-full" disabled={submitting}>
                    {submitting ? "Uploading..." : "Request My Free Audit"} <ArrowRight className="h-4 w-4" />
                  </Button>
                </form>
              ) : (
                <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="py-8 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/15"><CheckCircle2 className="h-8 w-8 text-success" /></div>
                  <h3 className="mt-5 font-display text-2xl font-semibold">Invoice received</h3>
                  <p className="mx-auto mt-2 max-w-sm text-muted-foreground">
                    Thanks, {form.name.split(" ")[0]}. Our analysts will review <span className="text-foreground">{form.fileName}</span> and email a findings summary to <span className="text-foreground">{form.email}</span> within one business day.
                  </p>
                  <div className="mt-6 rounded-xl border border-border bg-secondary/40 p-5 text-left">
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Preliminary estimate</p>
                    <p className="mt-1 text-sm text-foreground/90">Based on your spend tier, comparable audits recovered <span className="font-semibold text-success">20–40%</span> in the first pass.</p>
                  </div>
                  <Button variant="outline" className="mt-6" onClick={() => { setForm(EMPTY); setSelectedFile(null); setWebsite(""); setFormStartedAt(Date.now()); setDone(false); }}>Submit another invoice</Button>
                </motion.div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

const Field = ({ label, error, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm text-foreground/90">{label}</Label>
    {children}
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);
